import { useCallback, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { MdsModal } from "@/design-system/components/modal";
import type { AppId, ImportWizardProps } from "./ImportWizard.types";
import { WizardContainer } from "@/components/onboarding/components/ImportWizard/StyledComponents";
import { AnimatePresence, motion } from "motion/react";
import { mdsSpacings } from "@/design-system/foundations";
import { Tabs } from "@/components/onboarding/components/ImportWizard/Tabs";
import { Steps } from "@/components/onboarding/components/ImportWizard/ImportWizard.config";
import styled from "@emotion/styled";
import { trackEvent, TrackedEvent } from "@/domains/metrics";

export const ImportWizard = observer<ImportWizardProps>(({ isOpen, onClose }) => {
  const [selectedAppId, setSelectedAppId] = useState<AppId | null>(null);
  const [customAppName, setCustomAppName] = useState<string>("");
  const [contentHeight, setContentHeight] = useState<number>(360);
  const wizardContainerRef = useRef<HTMLDivElement>(null);
  const [wizardStep, _setWizardStep] = useState<string>("app-selection");
  const expStepRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setHeightForStep(wizardStep);
    }
  }, [isOpen, wizardStep]);

  const gotoWizardStep = useCallback(
    (step: string, nextAppId?: AppId) => {
      _setWizardStep(step);

      trackEvent(TrackedEvent.ImportWizardStepChange, {
        from: wizardStep,
        to: step,
        appId: nextAppId ?? selectedAppId,
      });
    },
    [selectedAppId, wizardStep]
  );

  const shouldShowTabsForStep = (step: string) => {
    return !["app-selection", "skip-confirmation"].includes(step);
  };

  const setHeightForStep = (step: string) => {
    const doWork = () => {
      const stepEl = document.querySelector(`[data-wizard-step="${step}"]`) as HTMLElement | null;
      if (stepEl) {
        const vPadding = 48; // 24 top and bottom
        const newHeight = stepEl.scrollHeight + vPadding;
        setContentHeight(newHeight);
      } else {
        requestAnimationFrame(doWork);
      }
    };
    requestAnimationFrame(doWork);
  };

  const handleClose = () => {
    onClose();
    trackEvent(TrackedEvent.ImportWizardComplete);
  };

  const handleAppSelect = (appId: AppId) => {
    setSelectedAppId(appId);
  };

  const handleCustomAppNameChange = (name: string) => {
    setCustomAppName(name);
  };

  const handleModalClose = () => {
    // Prevent closing the modal via ESC or clicking outside
    // Only allow closing via the Skip button
    // There are explicit "close" buttons in the various app-specific flows
    return;
  };

  const handleTabSelect = useCallback(
    (appId: AppId) => {
      let nextWizardStep = "other-apps-1";

      switch (appId) {
        case "apple-notes":
          nextWizardStep = "apple-notes-1";
          handleAppSelect(appId);
          setHeightForStep("apple-notes-1");
          break;
        case "obsidian":
          nextWizardStep = "obsidian-1";
          handleAppSelect(appId);
          setHeightForStep("obsidian-1");
          break;
        case "other":
          nextWizardStep = "other-apps-1";
          handleAppSelect(appId);
          setHeightForStep("other-apps-1");
          break;
        default:
          nextWizardStep = "other-apps-1";
          handleAppSelect("other");
          setHeightForStep("other-apps-1");
          break;
      }

      gotoWizardStep(nextWizardStep, appId);
    },
    [gotoWizardStep]
  );

  const handleSkip = useCallback(() => {
    gotoWizardStep("skip-confirmation");
    setHeightForStep("skip-confirmation");
  }, [gotoWizardStep]);

  return (
    <MdsModal
      isModalOpen={isOpen}
      handleCloseModal={handleModalClose}
      modalOverlayStyles={{
        background: "rgba(0, 0, 0, 0.08)",
      }}
      modalContentStyles={{
        overflow: "hidden",
      }}
    >
      <WizardContainer
        data-component="wizard-container"
        layout="position"
        layoutRoot
        layoutScroll
        animate={{ height: contentHeight + (shouldShowTabsForStep(wizardStep) ? 45 : 0) }}
        ref={wizardContainerRef}
      >
        {shouldShowTabsForStep(wizardStep) && (
          <Tabs selectedAppId={selectedAppId} onAppSelect={handleTabSelect} />
        )}
        {Object.entries(Steps).map(([stepName, stepData]) => {
          return (
            <AnimatePresence key={stepName}>
              {wizardStep === stepName && (
                <StepCrossFade
                  key={stepName}
                  layout="position"
                  ref={wizardStep === stepName ? expStepRef : undefined}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  top={shouldShowTabsForStep(stepName) ? "71px" : mdsSpacings().lg}
                  data-wizard-step={stepName}
                >
                  {stepData.render({
                    selectedAppId,
                    customAppName,
                    onNavToStep: (step: string) => {
                      gotoWizardStep(step);
                      setHeightForStep(step);
                    },
                    onSetSelectedApp: handleAppSelect,
                    onSetCustomAppName: handleCustomAppNameChange,
                    onClose: handleClose,
                    onSkip: handleSkip,
                  })}
                </StepCrossFade>
              )}
            </AnimatePresence>
          );
        })}
      </WizardContainer>
    </MdsModal>
  );
});

const StepCrossFade = styled(motion.div)<{ top: string }>`
  position: fixed;
  top: ${({ top }) => top};
  left: ${({ theme }) => theme.spacing.lg};
  right: ${({ theme }) => theme.spacing.lg};
`;
